/**
 * Глобальный контекст фоновых процессов (job'ов).
 *
 * Живёт на уровне оболочки (App), поэтому переживает закрытие модалки Sync Inbox
 * («Продолжить в фоне»), смену страницы и перезагрузку (F5): активные job'ы
 * подхватываются с бэка через GET /api/jobs?scope=recent.
 *
 * Отслеживает job'ы текущего пользователя (bulk-синхронизация с Jira, Hermes,
 * CSV-импорт и т.д.), показывает их в индикаторе рельса (JobsIndicator) и
 * уведомляет тостом о завершении.
 *
 * Паттерн скопирован с features/hermes/HermesSessionsContext.tsx.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { message } from 'antd';
import type { JobResponse, JobKind, JobStatus } from '@lens/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { listJobs, getJob, cancelJob } from './api.ts';

/** Человекочитаемое имя вида job'а (никогда не показываем сырой kind). */
const KIND_LABELS: Record<JobKind, string> = {
  bulk_jira_push: 'Синхронизация в Jira (поля)',
  bulk_jira_pull: 'Импорт из Jira (поля)',
  bulk_jira_pull_jql: 'Импорт из Jira по JQL',
  bulk_jira_links_push: 'Синхронизация связей в Jira',
  bulk_jira_links_pull: 'Импорт связей из Jira',
  bulk_jira_sync_both: 'Синхронизация с Jira (поля и связи)',
  hermes_estimate: 'Оценка Hermes',
  bulk_hermes_estimate: 'Массовая оценка Hermes',
  csv_import_items: 'Импорт записей из CSV',
  csv_import_dictionary: 'Импорт справочника из CSV',
  cleanup_sessions: 'Очистка сессий',
  cleanup_audit: 'Очистка журнала',
  cleanup_ai_log: 'Очистка AI-лога',
  automation_run: 'Автоматизация',
};

export interface UiJob {
  id: string;
  kind: JobKind;
  status: JobStatus;
  progress: number;
  total: number;
  spaceId: string | null;
  result: unknown;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** Человекочитаемая подпись (переданная при запуске или производная от kind). */
  label: string;
  /** pending | running. */
  active: boolean;
}

interface JobsContextValue {
  jobs: UiJob[];
  activeCount: number;
  open: boolean;
  showDock: () => void;
  closeDock: () => void;
  toggleDock: () => void;
  /** Зарегистрировать только что запущенный job (сразу подтягиваем статус). */
  registerJob: (jobId: string, label?: string) => void;
  /** Отменить pending/running job. */
  cancel: (jobId: string) => Promise<void>;
  /** Убрать завершённый job из индикатора (не отменяет). */
  dismiss: (jobId: string) => void;
}

const JobsCtx = createContext<JobsContextValue | null>(null);

const ACTIVE_STATUSES: readonly JobStatus[] = ['pending', 'running'];
const POLL_ACTIVE_MS = 1500;
const POLL_IDLE_MS = 20_000;

function isActive(status: JobStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

function labelFor(job: JobResponse, override?: string): string {
  return override ?? KIND_LABELS[job.kind] ?? 'Фоновая задача';
}

function toUiJob(job: JobResponse, label: string): UiJob {
  return {
    id: job.id,
    kind: job.kind,
    status: job.status,
    progress: job.progress,
    total: job.total,
    spaceId: job.spaceId,
    result: job.result,
    error: job.error,
    createdAt: job.createdAt,
    finishedAt: job.finishedAt,
    label,
    active: isActive(job.status),
  };
}

/** Активные сверху, затем по времени создания (свежие выше). */
function sortJobs(jobs: UiJob[]): UiJob[] {
  return [...jobs].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function JobsProvider({ children }: PropsWithChildren): JSX.Element {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<UiJob[]>([]);
  const [open, setOpen] = useState(false);

  // Подписи, переданные при запуске (id → label). Server-discovered job'ы без
  // подписи получают её из KIND_LABELS.
  const labelsRef = useRef<Map<string, string>>(new Map());
  // Скрытые пользователем завершённые job'ы — не показываем, даже если бэк ещё
  // возвращает их в окне 'recent'.
  const dismissedRef = useRef<Set<string>>(new Set());
  // Предыдущие статусы — чтобы поймать переход active → терминальный и дать тост.
  const prevStatusRef = useRef<Map<string, JobStatus>>(new Map());
  const jobsRef = useRef<UiJob[]>([]);
  jobsRef.current = jobs;

  // Слить входящие job'ы в состояние.
  //   prune=false — точечное обновление (registerJob): только добавить/обновить.
  //   prune=true  — полный опрос: дополнительно удалить терминальные job'ы,
  //                 которые бэк больше не отдаёт (вышли из окна 'recent'), чтобы
  //                 список не рос бесконечно. Активные не трогаем — их могло не
  //                 быть в конкретном ответе только при сбое запроса.
  const mergeJobs = useCallback((incoming: JobResponse[], prune: boolean) => {
    // Тосты о завершении (переход active → терминальный).
    for (const j of incoming) {
      const prev = prevStatusRef.current.get(j.id);
      if (prev && isActive(prev) && !isActive(j.status)) {
        const label = labelFor(j, labelsRef.current.get(j.id));
        if (j.status === 'success') void message.success(`${label}: готово`);
        else if (j.status === 'error') void message.error(`${label}: ошибка`);
      }
      prevStatusRef.current.set(j.id, j.status);
    }

    setJobs((prev) => {
      const byId = new Map(prev.map((j) => [j.id, j]));
      for (const j of incoming) {
        if (dismissedRef.current.has(j.id)) continue;
        byId.set(j.id, toUiJob(j, labelFor(j, labelsRef.current.get(j.id))));
      }
      if (prune) {
        const incomingIds = new Set(incoming.map((j) => j.id));
        for (const j of byId.values()) {
          if (!j.active && !incomingIds.has(j.id)) byId.delete(j.id);
        }
      }
      return sortJobs([...byId.values()]);
    });
  }, []);

  // Полный опрос списка (активные + недавние). Возвращает число активных.
  const refresh = useCallback(async (): Promise<number> => {
    try {
      const res = await listJobs('recent');
      mergeJobs(res.jobs, true);
      return res.jobs.filter((j) => isActive(j.status)).length;
    } catch {
      return jobsRef.current.filter((j) => j.active).length;
    }
  }, [mergeJobs]);

  const registerJob = useCallback(
    (jobId: string, label?: string) => {
      if (label) labelsRef.current.set(jobId, label);
      dismissedRef.current.delete(jobId);
      // Оптимистично подтягиваем статус, чтобы индикатор появился сразу.
      void getJob(jobId)
        .then((job) => mergeJobs([job], false))
        .catch(() => {
          /* следующий poll подхватит */
        });
    },
    [mergeJobs],
  );

  const cancel = useCallback(
    async (jobId: string) => {
      try {
        await cancelJob(jobId);
      } catch {
        void message.error('Не удалось отменить задачу');
      }
      await refresh();
    },
    [refresh],
  );

  const dismiss = useCallback((jobId: string) => {
    dismissedRef.current.add(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  // Опрос с адаптивным интервалом: часто, пока есть активные; редко в простое
  // (чтобы подхватить job'ы, запущенные в другой вкладке / до перезагрузки).
  useEffect(() => {
    if (!user) {
      setJobs([]);
      labelsRef.current.clear();
      dismissedRef.current.clear();
      prevStatusRef.current.clear();
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const activeN = await refresh();
      if (cancelled) return;
      timer = setTimeout(() => void tick(), activeN > 0 ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user, refresh]);

  const activeCount = useMemo(() => jobs.filter((j) => j.active).length, [jobs]);

  const showDock = useCallback(() => setOpen(true), []);
  const closeDock = useCallback(() => setOpen(false), []);
  const toggleDock = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo<JobsContextValue>(
    () => ({
      jobs,
      activeCount,
      open,
      showDock,
      closeDock,
      toggleDock,
      registerJob,
      cancel,
      dismiss,
    }),
    [jobs, activeCount, open, showDock, closeDock, toggleDock, registerJob, cancel, dismiss],
  );

  return <JobsCtx.Provider value={value}>{children}</JobsCtx.Provider>;
}

export function useJobs(): JobsContextValue {
  const ctx = useContext(JobsCtx);
  if (!ctx) throw new Error('useJobs must be used within JobsProvider');
  return ctx;
}
